// MJ PDP behavior: gallery sync, ONE purchase state machine, P01 sticky
// mobile ATC. State = { mode: 'single' | 'set', purchase: 'onetime' |
// 'subscribe' } driving the single product form: set mode (P08 checkbox)
// REPLACES the variant id — never adds on top — and reprices the purchase
// options from the set's own Seal plans (data contract: the
// [data-product-data] JSON rendered by sections/main-product.liquid).

if (!customElements.get('main-product')) {
  customElements.define(
    'main-product',
    class MJMainProduct extends HTMLElement {
      connectedCallback() {
        this.track = (event, payload) => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(Object.assign({ event: event }, payload || {}));
        };

        const dataEl = this.querySelector('[data-product-data]');
        this.data = dataEl ? JSON.parse(dataEl.textContent) : { single: null, set: null };

        this.form = this.querySelector('.ab-pdp__form form');
        this.idInput = this.form && this.form.querySelector('input[name="id"]');
        this.planInput = this.querySelector('[data-selling-plan-input]');
        this.atc = this.querySelector('[data-atc-button]');
        this.atcLabel = this.querySelector('[data-atc-label]');
        this.stickyTitle = this.querySelector('[data-sticky-title]');
        this.stickyPrice = this.querySelector('[data-sticky-price]');
        this.stickyButton = this.querySelector('[data-sticky-add]');
        this.upsell = this.querySelector('[data-upsell]');
        this.setToggle = this.querySelector('[data-upsell-toggle]');
        this.frequency = this.querySelector('[data-frequency]');
        this.frequencySelect = this.querySelector('[data-frequency-select]');
        this.optionOnetime = this.querySelector('[data-option-onetime]');
        this.optionSubscribe = this.querySelector('[data-option-subscribe]');
        this.note = this.querySelector('[data-purchase-note]');
        this.radios = Array.from(
          this.querySelectorAll('[data-purchase-options] input[type="radio"]')
        );

        this.state = { mode: 'single', purchase: 'onetime' };
        this.autoSwitched = false; // set mode dropped a chosen subscription

        this.setupGallery();
        this.setupPurchaseState();
        this.setupUpsell();
        this.setupStickyAtc();
        this.setupHowtoAlign();
        this.applyState();
      }

      /* ---------- state helpers ---------- */

      /* "Delivery every 1 month" / "1 month" → "1 month": how single ↔ set plans match */
      normalizeInterval(name) {
        return String(name)
          .toLowerCase()
          .replace(/deliver(?:y)? every/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      product() {
        return this.state.mode === 'set' && this.data.set ? this.data.set : this.data.single;
      }

      planFor(product, interval) {
        if (!product || !interval) return null;
        return (
          product.plans.find((p) => this.normalizeInterval(p.name) === interval) || null
        );
      }

      selectedInterval() {
        const option = this.frequencySelect && this.frequencySelect.selectedOptions[0];
        return option ? this.normalizeInterval(option.textContent) : null;
      }

      /* Single source of truth: form inputs, option prices, labels, sticky bar */
      applyState() {
        const product = this.product();
        if (!product) return;
        const setMode = this.state.mode === 'set';

        // Frequency options = the intervals the current product actually offers
        // (the select lists the single's plans; set mode hides the ones the set lacks).
        let offered = [];
        if (this.frequencySelect) {
          offered = Array.from(this.frequencySelect.options).filter((option) => {
            const ok = !!this.planFor(product, this.normalizeInterval(option.textContent));
            option.hidden = !ok;
            option.disabled = !ok;
            return ok;
          });
          const current = this.frequencySelect.selectedOptions[0];
          if ((!current || current.disabled) && offered[0]) {
            this.frequencySelect.value = offered[0].value;
          }
        }
        const canSubscribe = this.radios.length > 0 && offered.length > 0;

        // Set mode can't honour a subscription the set doesn't offer: fall back
        // to one-time and say so (role="status" note).
        if (this.state.purchase === 'subscribe' && !canSubscribe) {
          this.state.purchase = 'onetime';
          this.autoSwitched = true;
        }
        const subscribing = this.state.purchase === 'subscribe';
        const plan = subscribing ? this.planFor(product, this.selectedInterval()) : null;

        // Purchase option cards
        if (this.optionOnetime) {
          this.optionOnetime.querySelector('input').checked = !subscribing;
          this.optionOnetime.querySelector('[data-option-onetime-price]').textContent =
            product.priceFormatted;
        }
        if (this.optionSubscribe) {
          const radio = this.optionSubscribe.querySelector('input');
          radio.checked = subscribing;
          radio.disabled = !canSubscribe;
          this.optionSubscribe.classList.toggle('is-disabled', !canSubscribe);
          const shown = plan || this.planFor(product, this.selectedInterval());
          this.optionSubscribe.querySelector('[data-option-subscribe-price]').textContent =
            shown ? shown.priceFormatted : product.priceFormatted;
          this.optionSubscribe.querySelector('[data-option-subscribe-sub]').textContent = shown
            ? 'Save ' + shown.saveFormatted + '\u00a0· Skip\u00a0or\u00a0cancel\u00a0anytime' // wraps only after the dot
            : 'Sets ship as a one-time order';
          const title = this.optionSubscribe.querySelector('[data-option-subscribe-title]');
          if (title && shown && shown.pct > 0) {
            title.textContent = title.dataset.planName + ' ' + shown.pct + '%';
          }
        }
        // Separate-price value struck beside both card prices (sets only)
        this.querySelectorAll('[data-option-was]').forEach((was) => {
          was.hidden = !product.valueFormatted;
          if (product.valueFormatted) was.textContent = product.valueFormatted;
        });
        if (this.note) this.note.hidden = !this.autoSwitched;
        if (this.frequency) this.setFrequencyOpen(subscribing);

        // The one form
        if (this.idInput) this.idInput.value = product.variantId;
        if (this.planInput) this.planInput.value = plan ? plan.id : '';

        // Labels + sticky bar
        const key = subscribing ? 'Subscribe' : setMode ? 'Set' : 'Onetime';
        const price = plan ? plan.priceFormatted : product.priceFormatted;
        if (this.atcLabel && this.atc && !this.atc.disabled) {
          this.atcLabel.textContent = this.atc.dataset['label' + key] + ' — ' + price;
        }
        if (this.stickyTitle) this.stickyTitle.textContent = product.title;
        if (this.stickyPrice) this.stickyPrice.textContent = price;
        if (this.stickyButton && !this.stickyButton.disabled) {
          this.stickyButton.textContent = this.stickyButton.dataset['label' + key];
        }

        if (this.upsell) this.upsell.classList.toggle('is-selected', setMode);
        if (this.setToggle) this.setToggle.checked = setMode;
      }

      /* "Deliver every" reveal: CSS animates grid-template-rows 0fr→1fr on
         .is-open; inert + aria-hidden keep the collapsed select out of the tab
         order and the accessibility tree (visibility:hidden covers browsers
         without inert). */
      setFrequencyOpen(open) {
        this.frequency.classList.toggle('is-open', open);
        this.frequency.toggleAttribute('inert', !open);
        this.frequency.setAttribute('aria-hidden', open ? 'false' : 'true');
      }

      setupPurchaseState() {
        this.radios.forEach((radio) =>
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            this.autoSwitched = false;
            this.state.purchase = radio.value === 'subscribe' ? 'subscribe' : 'onetime';
            this.applyState();
          })
        );
        if (this.frequencySelect) {
          this.frequencySelect.addEventListener('change', () => this.applyState());
        }
      }

      /* P08 checkbox card: the whole card is the click target */
      setupUpsell() {
        if (!this.upsell || !this.setToggle) return;

        // set_upgrade_view fires when the card is actually seen
        if ('IntersectionObserver' in window) {
          const viewObserver = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
              this.track('set_upgrade_view');
              viewObserver.disconnect();
            }
          }, { threshold: 0.4 });
          viewObserver.observe(this.upsell);
        }

        this.setToggle.addEventListener('change', () => {
          this.autoSwitched = false;
          this.state.mode = this.setToggle.checked && this.data.set ? 'set' : 'single';
          if (this.state.mode === 'set') this.track('set_upgrade_select');
          this.applyState();
        });
        // Learn more: button + panel animated with a grid-row transition (CSS);
        // the panel is inert while closed (visibility) so its text is not tabbable.
        // Nothing else moves and the panel always opens DOWNWARD: on two columns
        // the sticky media block may already sit against the section end, where
        // extra height would shove it up. The grid's third row (--ab-pdp-extra)
        // takes exactly the panel's overflow past the section end and transitions
        // with the same timing as the panel, so the button never moves.
        const moreBtn = this.upsell.querySelector('[data-upsell-more-btn]');
        const morePanel = this.upsell.querySelector('[data-upsell-more]');
        const grid = this.querySelector('.ab-pdp__grid');
        const media = this.querySelector('.ab-pdp__media');
        const twoCol = window.matchMedia('(min-width: 990px)');
        if (moreBtn && morePanel) {
          moreBtn.addEventListener('click', () => {
            const open = moreBtn.getAttribute('aria-expanded') !== 'true';
            if (grid && media && twoCol.matches) {
              if (open) {
                const overflow =
                  media.getBoundingClientRect().bottom +
                  morePanel.firstElementChild.scrollHeight -
                  grid.getBoundingClientRect().bottom;
                grid.style.setProperty('--ab-pdp-extra', Math.max(0, Math.ceil(overflow)) + 'px');
              } else {
                grid.style.removeProperty('--ab-pdp-extra');
              }
            }
            moreBtn.setAttribute('aria-expanded', String(open));
            morePanel.classList.toggle('is-open', open);
          });
        }
        this.upsell.addEventListener('click', (event) => {
          if (this.setToggle.disabled) return;
          if (event.target.closest('input, label, a, button')) return; // native controls keep their own handling; the open learn-more panel is card, so it toggles too
          this.setToggle.click();
        });
        if (this.form) {
          this.form.addEventListener('submit', () => {
            if (this.state.mode === 'set') this.track('set_upgrade_add_to_cart');
          });
        }
      }

      /* Gallery: thumbs + dots track the scroll-snap stage */
      setupGallery() {
        const stage = this.querySelector('[data-stage]');
        if (!stage) return;
        const thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
        const dots = Array.from(this.querySelectorAll('[data-dot]'));
        const arrows = Array.from(this.querySelectorAll('[data-arrow]'));
        const slides = Array.from(stage.querySelectorAll('[data-slide]'));
        const gallery = this.querySelector('[data-gallery]');
        let current = 0;

        const setActive = (index) => {
          current = index;
          slides.forEach((s, i) => i !== index && s.querySelector('video')?.pause());
          gallery?.classList.toggle('is-video', !!slides[index]?.hasAttribute('data-founder'));
          thumbs.forEach((t, i) => {
            t.classList.toggle('is-active', i === index);
            if (i === index) t.setAttribute('aria-current', 'true');
            else t.removeAttribute('aria-current');
          });
          dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
          arrows.forEach((a) => {
            const target = index + Number(a.dataset.arrow);
            const hide = target < 0 || target >= slides.length;
            // An arrow hidden while it holds focus would drop focus to <body>
            // (Tab landed on it in the 80ms before the scroll handler ran).
            if (hide && document.activeElement === a) stage.focus({ preventScroll: true });
            a.classList.toggle('is-hidden', hide);
          });
        };

        const goTo = (index) => {
          if (!slides[index]) return;
          stage.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
          setActive(index);
        };

        /* Founder video: the overlay hands over to native controls on first play */
        stage.querySelectorAll('[data-founder]').forEach((slide) => {
          const video = slide.querySelector('video');
          slide.querySelector('[data-play]').addEventListener('click', () => {
            slide.classList.add('is-playing');
            video.controls = true;
            video.play();
            video.focus(); /* the overlay hides itself; keep keyboard focus on the player */
          });
        });

        thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => goTo(index)));
        arrows.forEach((a) => a.addEventListener('click', () => goTo(current + Number(a.dataset.arrow))));

        let scrollTimer = null;
        stage.addEventListener(
          'scroll',
          () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
              const index = Math.round(stage.scrollLeft / stage.clientWidth);
              setActive(Math.min(index, slides.length - 1));
            }, 80);
          },
          { passive: true }
        );
      }

      /* Two-column only: the P08 card (media column) and the P07 usage grid
         (buybox) share a horizontal line on first paint. The buybox copy above
         the grid varies per product, so the grid is pushed DOWN by the gap
         (never pulled up). Both columns start at the same grid row, so the
         card and grid offsets are measured against their own column tops —
         the sticky media block's scroll offset cancels out. Re-run on resize,
         font load, and any buybox/media size change (Judge.me hydrates the
         rating row late); writes only when the value moves. */
      setupHowtoAlign() {
        const howto = this.querySelector('.ab-pdp__buybox .ab-pdp__howto');
        const card = this.querySelector('.ab-pdp__media .ab-pdp__upsell');
        const media = this.querySelector('.ab-pdp__media');
        const buybox = this.querySelector('.ab-pdp__buybox');
        if (!howto || !card || !media || !buybox) return;

        const mq = window.matchMedia('(min-width: 990px)');
        let ticking = false;
        let applied = 0;
        const update = () => {
          ticking = false;
          if (!mq.matches) {
            if (applied) { applied = 0; howto.style.marginTop = ''; }
            return;
          }
          const cardTop = card.getBoundingClientRect().top - media.getBoundingClientRect().top;
          const howtoTop = howto.getBoundingClientRect().top - buybox.getBoundingClientRect().top - applied;
          const next = Math.max(0, Math.round(cardTop - howtoTop));
          if (Math.abs(next - applied) < 1) return;
          applied = next;
          howto.style.marginTop = next ? next + 'px' : '';
        };
        const requestUpdate = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };
        window.addEventListener('resize', requestUpdate);
        window.addEventListener('load', requestUpdate);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(requestUpdate);
        if ('ResizeObserver' in window) {
          const ro = new ResizeObserver(requestUpdate);
          ro.observe(buybox);
          ro.observe(media);
        }
        requestUpdate();
      }

      /* P01: sticky bar appears when the main ATC scrolls out of view (mobile)
         and retires once the footer is on screen, so it never sits over the
         footer's links or the copyright. Computed imperatively on
         scroll/resize/hashchange — an IntersectionObserver alone never fires
         when an anchor jump skips PAST the ATC without it ever entering the
         viewport (e.g. the "View ingredients" #ab-inside link), which left the
         bar permanently hidden.
         Hidden = inert + aria-hidden (the button leaves the Tab order and the
         accessibility tree together with the bar). Shown = the document's
         scroll-padding-bottom equals the bar height (--ab-sticky-atc-h, read by
         ab-main-product.css), so focus landing near the bottom of the viewport
         is scrolled clear of the bar (2.4.11). */
      setupStickyAtc() {
        const sticky = this.querySelector('[data-sticky-atc]');
        if (!sticky || !this.atc) return;
        const footer = document.querySelector('.shopify-section-group-footer-group, footer');
        const root = document.documentElement;

        let viewTracked = false;
        let ticking = false;
        let shown = null;
        const update = () => {
          ticking = false;
          const rect = this.atc.getBoundingClientRect();
          const footerTop = footer ? footer.getBoundingClientRect().top : Infinity;
          const show = rect.bottom < 0 && footerTop > window.innerHeight;
          if (show !== shown) {
            shown = show;
            sticky.classList.toggle('is-visible', show);
            sticky.toggleAttribute('inert', !show);
            sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          }
          if (show) root.style.setProperty('--ab-sticky-atc-h', sticky.offsetHeight + 'px');
          else root.style.removeProperty('--ab-sticky-atc-h');
          if (show && !viewTracked) {
            viewTracked = true;
            this.track('sticky_atc_view');
          }
        };
        const requestUpdate = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        };
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);
        window.addEventListener('hashchange', requestUpdate);
        requestUpdate();

        if (this.stickyButton) {
          this.stickyButton.addEventListener('click', () => {
            this.track('sticky_atc_click');
            if (this.form) this.form.requestSubmit(this.atc);
          });
        }
      }
    }
  );
}
